import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Validation = () => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleValidate = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter a validation code');
      return;
    }

    // Secret admin access code
    if (code.trim().toUpperCase() === 'ADMIN-ACCESS') {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/validate-code`, {
        code: code.trim(),
      });

      setResult({
        valid: response.data.valid,
        message: response.data.message,
        usageCount: response.data.usageCount,
      });
      setCode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Validation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="validation" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20"></div>
      <div className="absolute bottom-10 right-10 w-72 h-72 bg-sky-200 rounded-full mix-blend-multiply filter blur-xl opacity-20"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-blue-900 rounded-3xl shadow-lg">
              <Shield className="w-12 h-12 text-white" />
            </div>
          </div>
          <h2 className="text-5xl sm:text-6xl font-bold text-blue-900 mb-6">
            Product Validation
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sky-400 to-blue-900 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Verify the authenticity of your BIO-PEPTIDE product using the unique code on your packaging
          </p>
        </div>

        {/* Validation Form */}
        <div className="bg-white rounded-3xl shadow-2xl border-2 border-blue-100 p-8 sm:p-12">
          <form onSubmit={handleValidate} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-lg font-semibold text-blue-900 mb-3">
                Enter Validation Code
              </label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXXX-XXXXX-XXXXX"
                className="text-lg py-6 px-4 border-2 border-blue-200 focus:border-blue-500 rounded-xl"
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-lg font-semibold bg-blue-900 hover:bg-blue-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5 mr-2" />
                  Validate Product
                </>
              )}
            </Button>
          </form>

          {/* Error Message */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div
              className={`mt-6 p-6 rounded-xl border-2 ${
                result.valid
                  ? 'bg-green-50 border-green-300'
                  : 'bg-red-50 border-red-300'
              } transform transition-all duration-500 animate-in`}
            >
              <div className="flex items-start gap-4">
                {result.valid ? (
                  <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <h3
                    className={`text-2xl font-bold mb-2 ${
                      result.valid ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    {result.valid ? 'Valid' : 'Invalid'}
                  </h3>
                  <p
                    className={`text-lg ${
                      result.valid ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {result.message}
                  </p>
                  {result.valid && result.usageCount !== undefined && (
                    <p className="mt-2 text-sm text-green-600">
                      Usage count: {result.usageCount} / 2
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info boxes */}
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-blue-100 shadow-md">
            <h4 className="font-bold text-blue-900 mb-2">Where to find your code?</h4>
            <p className="text-gray-600 text-sm">
              Your unique validation code is printed on the product packaging or included in the product documentation.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-blue-100 shadow-md">
            <h4 className="font-bold text-blue-900 mb-2">Code can be used twice</h4>
            <p className="text-gray-600 text-sm">
              Each validation code can be verified up to 2 times for your convenience.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Validation;
